/**
 * Lock state persistence (localStorage + BroadcastChannel).
 * Survives refresh so lock screen shows again after reload.
 * Display cache allows the overlay to show correct mode/labels when session is invalidated.
 */

const LOCK_KEY = "happydoc_lock_state";
const LOCK_DISPLAY_KEY = "happydoc_lock_display";
const CHANNEL_NAME = "happydoc_lock_sync";

export interface LockState {
    isLocked: boolean;
    lockedAt: number;
}

export function getStoredLockState(): LockState {
    try {
        const raw = localStorage.getItem(LOCK_KEY);
        if (!raw) return { isLocked: false, lockedAt: 0 };
        const parsed = JSON.parse(raw) as LockState;
        return {
            isLocked: Boolean(parsed?.isLocked),
            lockedAt: typeof parsed?.lockedAt === "number" ? parsed.lockedAt : 0,
        };
    } catch {
        // Fail-closed: when storage is unreadable, assume locked
        return { isLocked: true, lockedAt: 0 };
    }
}

export function setStoredLockState(state: LockState): void {
    localStorage.setItem(LOCK_KEY, JSON.stringify(state));
}

export function clearStoredLockState(): void {
    localStorage.removeItem(LOCK_KEY);
    localStorage.removeItem(LOCK_DISPLAY_KEY);
}

export interface LockDisplayCache {
    device_access_mode: "pin" | "switch";
    clinic_name: string;
    clinic_location: string | null;
}

export function setLockDisplayCache(cache: LockDisplayCache): void {
    try {
        localStorage.setItem(LOCK_DISPLAY_KEY, JSON.stringify(cache));
    } catch {
        // ignore
    }
}

export function getLockDisplayCache(): LockDisplayCache | null {
    try {
        const raw = localStorage.getItem(LOCK_DISPLAY_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as LockDisplayCache;
        if (parsed?.device_access_mode !== "pin" && parsed?.device_access_mode !== "switch") return null;
        return parsed;
    } catch {
        return null;
    }
}

export type LockSyncMessage =
    | { type: "lock" }
    | { type: "unlock"; userId?: string }
    | { type: "user_switch"; userId: string };

/**
 * Returns a new BroadcastChannel for lock sync. Caller must call channel.close() when done
 * (e.g. on unmount) so the channel does not outlive its listener and drop messages.
 */
export function getLockBroadcastChannel(): BroadcastChannel | null {
    if (typeof BroadcastChannel === "undefined") return null;
    return new BroadcastChannel(CHANNEL_NAME);
}
